// Corner snapping (CO-5854): while a product is dragged,
// a corner of it that comes close on screen to an outside corner of another
// product, at the same level, moves the product so the two meet, and holds
// until pulled clearly away. Only outside corners of the carcase and fronts
// count, the inside corner of an L included, and a corner never puts the
// product inside another. The range is set on screen, so it feels the same at
// any zoom, and never reaches past the flush snap's 110 mm.
import * as THREE from 'three';
import {FILLER_SNAPPING_DISTANCE} from 'components/customer/RoomPlanner/helpers/snapping/constants';
import type {SurfaceFacts} from 'components/customer/RoomPlanner/helpers/snapping/types';
import type {AlignmentBody} from 'components/customer/RoomPlanner/helpers/movement/faceAlignment';
import {
    BUTT_TOLERANCE,
    overlappingOf,
} from 'components/customer/RoomPlanner/helpers/movement/butting';

// How near on screen, in pixels, two corners must come to meet, and how far
// apart they may then pull before letting go.
export const CORNER_ENTER_PX = 8;
export const CORNER_RELEASE_PX = 14;

// Corners closer than this (0.5 mm) are one corner.
const MERGE_DISTANCE = 0.1;

// How far around a corner the space is probed to tell whether it is an
// outside corner (5 mm), past the gaps between components that the faces
// treat as closed.
const PROBE = 1;

// Two heights this close (2.5 mm) are the same level.
const LEVEL_TOLERANCE = 0.5;

/**
 * The view a drag is seen in, which sets how near on screen is near.
 */
export interface CornerView {
    camera: THREE.Camera;
    // The canvas size in pixels.
    viewport: THREE.Vector2;
}

/**
 * A corner snap a drag is holding: the other product, its corner, and which
 * corner of the dragged product's box is on it.
 */
export interface HeldCorner {
    group: THREE.Group;
    vertex: THREE.Vector3;
    corner: number;
}

/**
 * A corner snap: what to add to the drag target to land on it, how far that
 * moves the product, the product it meets, and what to hold next move.
 */
export interface CornerSnap {
    offset: THREE.Vector3;
    distance: number;
    other: SurfaceFacts;
    held: HeldCorner;
}

/**
 * @param {THREE.Box3} bbox A box.
 * @return {THREE.Vector3[]} Its eight corners, bit 0 for x, 1 for y, 2 for z.
 */
export const cornersOf = (bbox: THREE.Box3): THREE.Vector3[] =>
    [0, 1, 2, 3, 4, 5, 6, 7].map(
        (i) =>
            new THREE.Vector3(
                i & 1 ? bbox.max.x : bbox.min.x,
                i & 2 ? bbox.max.y : bbox.min.y,
                i & 4 ? bbox.max.z : bbox.min.z
            )
    );

const strictlyInside = (bbox: THREE.Box3, point: THREE.Vector3) =>
    bbox.min.x < point.x &&
    point.x < bbox.max.x &&
    bbox.min.y < point.y &&
    point.y < bbox.max.y &&
    bbox.min.z < point.z &&
    point.z < bbox.max.z;

/**
 * @param {THREE.Vector3} point A point on the product's outline.
 * @param {THREE.Box3[]} solids The product's solid boxes.
 * @return {boolean} True when the point is a corner of the outline: the
 * space PROBE around it changes along every axis, so it is neither on a flat
 * face, nor along a straight edge, nor inside the product.
 */
const isCorner = (
    point: THREE.Vector3,
    solids: readonly THREE.Box3[]
): boolean => {
    const filled = cornersOf(
        new THREE.Box3(
            point.clone().subScalar(PROBE),
            point.clone().addScalar(PROBE)
        )
    ).map((probe) => solids.some((solid) => strictlyInside(solid, probe)));
    if (filled.every(Boolean) || !filled.some(Boolean)) return false;

    return [1, 2, 4].every((bit) =>
        filled.some((full, i) => full !== filled[i ^ bit])
    );
};

// Each product's corners, worked out once: its solid boxes stay the same
// object for as long as its facts are kept.
const cornerCache = new WeakMap<readonly THREE.Box3[], THREE.Vector3[]>();

/**
 * A product's outside corners: the corners of its solid boxes that are
 * corners of its outline, merged where they coincide.
 * @param {THREE.Box3[]} solids The product's solid boxes.
 * @return {THREE.Vector3[]} The corners.
 */
export const exteriorCorners = (
    solids: readonly THREE.Box3[]
): THREE.Vector3[] => {
    const cached = cornerCache.get(solids);
    if (cached) return cached;

    const corners: THREE.Vector3[] = [];
    solids.forEach((solid) => {
        cornersOf(solid).forEach((corner) => {
            if (corners.some((c) => c.distanceTo(corner) < MERGE_DISTANCE)) {
                return;
            }
            if (isCorner(corner, solids)) corners.push(corner);
        });
    });
    cornerCache.set(solids, corners);

    return corners;
};

/**
 * @param {THREE.Vector3} point A point in the room.
 * @param {CornerView} view The view.
 * @return {THREE.Vector2} Where it is on screen, in pixels.
 */
const onScreen = (point: THREE.Vector3, {camera, viewport}: CornerView) => {
    const ndc = point.clone().project(camera);

    return new THREE.Vector2(
        ((ndc.x + 1) / 2) * viewport.x,
        ((1 - ndc.y) / 2) * viewport.y
    );
};

/**
 * The corner snap for a dragged product, or null: the corners nearest on
 * screen within CORNER_ENTER_PX, or the corner already held while within
 * CORNER_RELEASE_PX and no other is nearer. Matched in plan at the product's own level, for a free
 * move; refused where it would land the product inside the other.
 * @param {AlignmentBody} moving The dragged product where the drag puts it.
 * @param {SurfaceFacts[]} others The products and walls it may snap to.
 * @param {CornerView} view The view.
 * @param {HeldCorner | null} held The corner held last move.
 * @return {CornerSnap | null} The snap.
 */
export const findCornerSnap = (
    moving: AlignmentBody,
    others: readonly SurfaceFacts[],
    view: CornerView,
    held: HeldCorner | null
): CornerSnap | null => {
    const corners = cornersOf(moving.bbox);
    const screenCorners = corners.map((corner) => onScreen(corner, view));

    const snapAt = (
        other: SurfaceFacts,
        vertex: THREE.Vector3,
        cornerIndex: number,
        limitPx: number
    ): CornerSnap | null => {
        const corner = corners.at(cornerIndex);
        const screenCorner = screenCorners.at(cornerIndex);
        if (!corner || !screenCorner) return null;
        if (Math.abs(vertex.y - corner.y) > LEVEL_TOLERANCE) return null;
        if (onScreen(vertex, view).distanceTo(screenCorner) >= limitPx) {
            return null;
        }

        const offset = vertex.clone().sub(corner).setY(0);
        const distance = offset.length();
        if (distance >= FILLER_SNAPPING_DISTANCE) return null;
        const lands = (moving.solids ?? [moving.bbox]).map((solid) =>
            solid.clone().translate(offset)
        );
        const inside = overlappingOf(
            lands,
            [{product: other.group, bboxes: other.solidBBoxes}],
            BUTT_TOLERANCE
        ).length;
        if (inside) return null;

        return {
            offset,
            distance,
            other,
            held: {group: other.group, vertex, corner: cornerIndex},
        };
    };

    let best: CornerSnap | null = null;
    let bestPx = Infinity;
    others.forEach((other) => {
        exteriorCorners(other.solidBBoxes).forEach((vertex) => {
            const screenVertex = onScreen(vertex, view);
            screenCorners.forEach((screenCorner, cornerIndex) => {
                const px = screenVertex.distanceTo(screenCorner);
                if (px >= CORNER_ENTER_PX || px >= bestPx) return;

                const snap = snapAt(
                    other,
                    vertex,
                    cornerIndex,
                    CORNER_ENTER_PX
                );
                if (snap) {
                    best = snap;
                    bestPx = px;
                }
            });
        });
    });

    // The corner held last move stays while within CORNER_RELEASE_PX and no
    // other is nearer on screen, so it doesn't hop between corners as near
    // as each other, but gives way to a nearer one at once, so corners close
    // together can each be met (CO-5858).
    if (held) {
        const other = others.find(({group}) => group === held.group);
        const kept =
            other && snapAt(other, held.vertex, held.corner, CORNER_RELEASE_PX);
        const keptPx = screenCorners
            .at(held.corner)
            ?.distanceTo(onScreen(held.vertex, view));
        if (kept && keptPx !== undefined && keptPx <= bestPx) return kept;
    }

    return best;
};
